import { z } from "zod";
import { CONTACT_IMPORT_BIRTHDAY_FORMATS } from "./contact-import-birthday.js";
import { contactImportColumnMappingSchema } from "./contact-import-mapping.js";
import { apiBigIntIdSchema, apiIntIdSchema } from "./ids.js";
export const CONTACT_IMPORT_JOB_STATUSES = [
    "UPLOADED",
    "PREPARING",
    "AWAITING_CONFIRMATION",
    "PROCESSING",
    "CANCEL_REQUESTED",
    "COMPLETED",
    "CANCELLED",
    "FAILED",
];
export const CONTACT_IMPORT_SOURCE_FORMATS = ["CSV", "XLSX"];
export const CONTACT_IMPORT_JOB_ACTIVE_STATUSES = [
    "UPLOADED",
    "PREPARING",
    "AWAITING_CONFIRMATION",
    "PROCESSING",
    "CANCEL_REQUESTED",
];
export function isContactImportJobActiveStatus(status) {
    return CONTACT_IMPORT_JOB_ACTIVE_STATUSES.includes(status);
}
export function isContactImportJobTerminalStatus(status) {
    return status === "COMPLETED" || status === "CANCELLED" || status === "FAILED";
}
/** Preview/mapping states. These are active jobs, but not an executing import. */
export function isContactImportJobPreviewStatus(status) {
    return status === "UPLOADED" || status === "PREPARING" || status === "AWAITING_CONFIRMATION";
}
/** Confirmed import execution. The sticky progress toast belongs only here. */
export function isContactImportJobExecutionStatus(status) {
    return status === "PROCESSING" || status === "CANCEL_REQUESTED";
}
export function shouldShowContactImportProgressToast(status) {
    return isContactImportJobExecutionStatus(status);
}
/**
 * Poll GET /jobs/:id while the job is preparing or executing.
 * Mapping (`AWAITING_CONFIRMATION`) is static. Terminal jobs must not be polled.
 */
export function shouldPollContactImportJob(status) {
    return (status === "UPLOADED" ||
        status === "PREPARING" ||
        status === "PROCESSING" ||
        status === "CANCEL_REQUESTED");
}
export function shouldContinueContactImportPolling(job) {
    return job != null && shouldPollContactImportJob(job.status);
}
export const contactImportJobPreviewSchema = z.object({
    version: z.literal(1),
    sampleRows: z.array(z.array(z.string())).max(20),
    emptyIdentityCount: z.number().int().nonnegative(),
    invalidPhoneCount: z.number().int().nonnegative(),
    invalidEmailCount: z.number().int().nonnegative(),
    /** Birthdays left unset because the cell was not a usable date. Contacts were still written. */
    birthdaySkippedCount: z.number().int().nonnegative().optional(),
});
export const contactImportJobDtoSchema = z.object({
    id: apiBigIntIdSchema,
    organizationId: apiIntIdSchema,
    status: z.enum(CONTACT_IMPORT_JOB_STATUSES),
    sourceFormat: z.enum(CONTACT_IMPORT_SOURCE_FORMATS),
    originalFileName: z.string(),
    byteSize: z.number().int().nonnegative(),
    headers: z.array(z.string()).nullable(),
    suggestedMapping: contactImportColumnMappingSchema.nullable(),
    columnMapping: contactImportColumnMappingSchema.nullable(),
    /** Null until a mapping is stored. Legacy array mappings mean Auto. */
    birthdayFormat: z.enum(CONTACT_IMPORT_BIRTHDAY_FORMATS).nullable(),
    defaultCountryIso: z.string().nullable(),
    autoLinkExactPhoneMatches: z.boolean(),
    applyGroupIds: z.array(apiIntIdSchema),
    preview: contactImportJobPreviewSchema.nullable(),
    totalRows: z.number().int().nonnegative().nullable(),
    processedRows: z.number().int().nonnegative(),
    createdCount: z.number().int().nonnegative(),
    restoredCount: z.number().int().nonnegative(),
    skippedCount: z.number().int().nonnegative(),
    failedCount: z.number().int().nonnegative(),
    errorCode: z.string().nullable(),
    errorSummary: z.string().nullable(),
    startedAt: z.string().nullable(),
    completedAt: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const contactImportJobEnvelopeSchema = z.object({
    job: contactImportJobDtoSchema,
    created: z.boolean(),
});
export const contactImportActiveJobResponseSchema = z.object({
    job: contactImportJobDtoSchema.nullable(),
});
export const contactImportJobConfirmRequestSchema = z.object({
    columnMapping: contactImportColumnMappingSchema,
    birthdayFormat: z.enum(CONTACT_IMPORT_BIRTHDAY_FORMATS).optional(),
    defaultCountryIso: z.string().trim().length(2).toUpperCase(),
    autoLinkExactPhoneMatches: z.boolean().optional(),
    applyGroupIds: z.array(apiIntIdSchema).max(50).optional(),
});
//# sourceMappingURL=contact-import-job-contracts.js.map